/* ── Document Viewer Modal ────────────────────────────────── */
import {useEffect, useState} from "react";
import ApiClient from "../api";
import {Modal, Typography} from "antd";
import {CloseOutlined, FilePdfOutlined, FolderOpenOutlined, LoadingOutlined} from "@ant-design/icons";
const  api_client = new ApiClient();
const {Text} = Typography;

export const DocumentViewerModal = ({ open, onClose, fileList = [] }) => {
    const [selectedIndex, setSelectedIndex]   = useState(0);
    const [previewUrl,    setPreviewUrl]       = useState(null);
    const [loadingUrl,    setLoadingUrl]       = useState(false);
    const [loadedCache,   setLoadedCache]      = useState({});   // index → resolved url

    const fetchPreview = async (index) => {
        if (loadedCache[index]) {
            setPreviewUrl(loadedCache[index]);
            return;
        }
        setLoadingUrl(true);
        setPreviewUrl(null);
        try {
            const res = await api_client.get_preview_url(fileList[index]);
            const url = res?.previewUrl;
            setLoadedCache(prev => ({ ...prev, [index]: url }));
            console.log(url);
            setPreviewUrl(url);
        } catch {
            console.error('Failed to load document preview.');
            setPreviewUrl(null);
        } finally {
            setLoadingUrl(false);
        }
    };

    // Load first doc when modal opens
    useEffect(() => {
        if (open && fileList.length > 0) {
            setSelectedIndex(0);
            setLoadedCache({});
            setPreviewUrl(null);
            fetchPreview(0);
        }
    }, [open]);

    const handleSelect = (index) => {
        setSelectedIndex(index);
        fetchPreview(index);
    };

    const getExt = (url) => url.match(/\.(pdf|jpe?g|png|gif|webp)/i)?.[1]?.toLowerCase() || 'pdf';
    const isImage = (url) => /\.(jpe?g|png|gif|webp)/i.test(url);

    return (
        <Modal
            open={open}
            onCancel={onClose}
            footer={null}
            width="90vw"
            centered
            destroyOnClose
            closeIcon={<CloseOutlined style={{ color: '#6B8585' }} />}
            styles={{
                content: { background: '#0A1010', border: '1px solid rgba(121,167,165,0.15)', borderRadius: 12, padding: 0, overflow: 'hidden' },
                header:  { background: '#0A1010', borderBottom: '1px solid rgba(121,167,165,0.1)', padding: '14px 24px', margin: 0 },
                mask:    { backdropFilter: 'blur(6px)', background: 'rgba(4,8,8,0.92)' },
                body:    { padding: 0, height: '85vh', display: 'flex', overflow: 'hidden' },
            }}
            title={
                <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                    <FolderOpenOutlined style={{ color: '#79A7A5', fontSize: 15 }} />
                    <Text style={{ color: '#F0F8F7', fontSize: 13, fontFamily: 'monospace', fontWeight: 600 }}>
                        Claim Documents
                    </Text>
                    <span style={{
                        background: 'rgba(121,167,165,0.08)', border: '1px solid rgba(121,167,165,0.2)',
                        borderRadius: 4, padding: '1px 8px', color: '#79A7A5', fontSize: 10, fontFamily: 'monospace',
                    }}>
                        {fileList.length} files
                    </span>
                </div>
            }
        >
            {/* ── Preview pane (left) ── */}
            <div style={{ flex: 1, display: 'flex', flexDirection: 'column', background: '#060C0C', overflow: 'hidden' }}>

                {/* Preview toolbar */}
                <div style={{
                    display: 'flex', alignItems: 'center', gap: 10,
                    padding: '8px 16px', borderBottom: '1px solid rgba(121,167,165,0.08)',
                    background: '#0A1010',
                }}>
                    <FilePdfOutlined style={{ color: '#3D5454', fontSize: 12 }} />
                    <Text style={{ color: '#3D5454', fontSize: 11, fontFamily: 'monospace' }}>
                        {fileList.length > 0 ? `${selectedIndex + 1}.${getExt(fileList[selectedIndex])}` : '—'}
                    </Text>
                    {loadingUrl && (
                        <span style={{ marginLeft: 'auto', display: 'flex', alignItems: 'center', gap: 6 }}>
                            <LoadingOutlined style={{ color: '#3D5454', fontSize: 11 }} />
                            <Text style={{ color: '#3D5454', fontSize: 10, fontFamily: 'monospace' }}>Loading…</Text>
                        </span>
                    )}
                </div>

                {/* Preview area */}
                <div style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', overflow: 'hidden', position: 'relative' }}>
                    {loadingUrl && (
                        <div style={{ position: 'absolute', inset: 0, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: 12, zIndex: 2 }}>
                            <LoadingOutlined style={{ color: '#79A7A5', fontSize: 28 }} />
                            <Text style={{ color: '#3D5454', fontSize: 11, fontFamily: 'monospace' }}>Fetching secure URL…</Text>
                        </div>
                    )}

                    {!loadingUrl && !previewUrl && (
                        <div style={{ textAlign: 'center' }}>
                            <FolderOpenOutlined style={{ color: '#1A2828', fontSize: 52, display: 'block', marginBottom: 12 }} />
                            <Text style={{ color: '#2A3A3A', fontSize: 12, fontFamily: 'monospace' }}>No document loaded</Text>
                        </div>
                    )}

                    {!loadingUrl && previewUrl && (
                        isImage(fileList[selectedIndex])
                            ? <img
                                src={previewUrl}
                                alt={`Document ${selectedIndex + 1}`}
                                style={{ maxWidth: '100%', maxHeight: '100%', objectFit: 'contain' }}
                            />
                            : <iframe
                                key={previewUrl}
                                src={previewUrl}
                                title={`Document ${selectedIndex + 1}`}
                                style={{ width: '100%', height: '100%', border: 'none' }}
                            />
                    )}
                </div>
            </div>

            {/* ── File list sidebar (right) ── */}
            <div style={{
                width: 190, minWidth: 190, display: 'flex', flexDirection: 'column',
                borderLeft: '1px solid rgba(121,167,165,0.1)', background: '#0D1515', overflow: 'hidden',
            }}>
                <div style={{ padding: '10px 12px 8px', borderBottom: '1px solid rgba(121,167,165,0.08)' }}>
                    <Text style={{ color: '#2A3A3A', fontSize: 9, fontFamily: 'monospace', textTransform: 'uppercase', letterSpacing: '0.8px' }}>
                        Documents
                    </Text>
                </div>

                <div style={{ flex: 1, overflowY: 'auto', padding: '8px' }}>
                    {fileList.map((url, i) => {
                        const isSelected = selectedIndex === i;
                        const ext        = getExt(url);
                        const isLoaded   = !!loadedCache[i];

                        return (
                            <button
                                key={i}
                                onClick={() => handleSelect(i)}
                                style={{
                                    display: 'flex', alignItems: 'center', gap: 9,
                                    width: '100%', padding: '9px 10px', marginBottom: 3,
                                    background:   isSelected ? 'rgba(121,167,165,0.1)'  : 'transparent',
                                    border:       `1px solid ${isSelected ? 'rgba(121,167,165,0.25)' : 'transparent'}`,
                                    borderRadius: 6, cursor: 'pointer', textAlign: 'left',
                                    transition: 'all 0.12s',
                                }}
                                onMouseEnter={e => { if (!isSelected) e.currentTarget.style.background = 'rgba(121,167,165,0.05)'; }}
                                onMouseLeave={e => { if (!isSelected) e.currentTarget.style.background = 'transparent'; }}
                            >
                                {/* Icon */}
                                <div style={{
                                    width: 28, height: 34, borderRadius: 3, flexShrink: 0,
                                    background: isSelected ? 'rgba(121,167,165,0.15)' : 'rgba(121,167,165,0.05)',
                                    border: `1px solid ${isSelected ? 'rgba(121,167,165,0.3)' : 'rgba(121,167,165,0.1)'}`,
                                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                                }}>
                                    <FilePdfOutlined style={{ color: isSelected ? '#79A7A5' : '#2A3A3A', fontSize: 11 }} />
                                </div>

                                {/* Label + status */}
                                <div style={{ flex: 1, minWidth: 0 }}>
                                    <Text style={{
                                        color: isSelected ? '#F0F8F7' : '#6B8585',
                                        fontSize: 11, fontFamily: 'monospace',
                                        display: 'block', fontWeight: isSelected ? 600 : 400,
                                    }}>
                                        {i + 1}.{ext}
                                    </Text>
                                    <Text style={{ color: '#1E3030', fontSize: 9, fontFamily: 'monospace' }}>
                                        {isLoaded ? '● cached' : '○ not loaded'}
                                    </Text>
                                </div>
                            </button>
                        );
                    })}
                </div>
            </div>
        </Modal>
    );
};